import { atomWithReset, useResetAtom } from "jotai/utils";
import { useCallback } from "react";
import type { z } from "zod";
import type {
  ReservedScheduleLinkedSchedule,
  reservedScheduleBasicInfo,
} from "@/lib/form-schemas/reserved-schedule-schema";

/**
 * O rascunho do formulário: os campos do schema **mais** uma marca de procedência.
 *
 * `modalityChosenExplicitly` não é campo de formulário — é o registro de que o campo de categoria
 * estava na tela quando o membro submeteu. Por isso mora aqui e não em `reservedScheduleBasicInfo`:
 * pô-lo no schema o tornaria um input com validação, e ele é a resposta a "o profissional escolheu
 * ou herdou a pré-seleção?", que só quem renderizou o formulário sabe.
 *
 * O tracking da confirmação depende dele para distinguir quem viu o campo de quem tinha uma opção
 * só — sem essa distinção não há como comparar o antes e o depois da entrega.
 */
type ReservedScheduleForm = z.infer<typeof reservedScheduleBasicInfo> & {
  modalityChosenExplicitly: boolean;
};

/**
 * Sentinela de "o formulário ainda não foi preenchido". Não é uma modalidade: os códigos válidos
 * começam em `1`.
 *
 * As telas a jusante guardam contra ele em vez de buscar horário ou simular preço com
 * `codModalidadePeriodoGarantido: 0` — requisição que o backend recusa e que chegaria ao membro como
 * "não há salas disponíveis", escondendo o problema real.
 */
export const NO_MODALITY_CHOSEN = 0;
export const reservedScheduleFormAtom = atomWithReset<ReservedScheduleForm>({
  modality: NO_MODALITY_CHOSEN,
  location: 0,
  locationName: "",
  roomType: 0,
  frequency: 0,
  dayOfWeek: 0,
  duration: "",
  modalityChosenExplicitly: false,
});

export const reservedScheduleSlotFormAtom = atomWithReset({
  data: "",
  horaInicio: "",
  horaFim: "",
  /**
   * O horário escolhido pegou preço de parceria? Vem da anotação do slot, decidida contra a
   * categoria escolhida.
   *
   * É a **única** coisa que diz se o PG criado saiu no multiplicador 1.0× em vez de 1.2× — sem ela
   * não há como medir a iniciativa depois. Mora no atom de slot, e não no de formulário, porque é
   * propriedade do horário: a mesma categoria rende `true` num horário e `false` no seguinte.
   */
  isPromotional: false,
  /**
   * A categoria promocional disponível **neste** horário, quando a escolhida não é promocional. Vem
   * do decorator, e é o gatilho da comparação de preço na confirmação.
   *
   * ⚠️ Os critérios do T8 diziam que este atom ganharia "só `isPromotional`". Não fecha: o gatilho
   * da tela de confirmação (T10) é `cheaperModality != null` no slot **escolhido**, e a confirmação
   * não recebe as anotações por slot — ela só tem o preço. Ou seja, ou o valor atravessa por aqui,
   * ou a comparação não existe.
   */
  cheaperModality: null as number | null,
});

export const reservedScheduleStartingDayFormAtom = atomWithReset({
  startingDay: new Date(),
});

/**
 * O pedido de abrir a agenda equivalente junto com o PG (KRA-447), com os tipos de atendimento
 * escolhidos. Preenchido pelo passo da T10, lido pela confirmação (T11) para montar o request — só
 * com a flag `abre-agenda-vinculada-ao-criar-periodo-garantido` ligada; ver
 * `linkedScheduleVariables`.
 *
 * O default é "pedir, sem tipo escolhido ainda": o passo nasce com o toggle ligado, porque um PG sem
 * agenda é sala paga e parada, e desligar é a exceção. Isso **não** muda o request de quem nunca viu
 * o passo — a confirmação e o helper de variáveis só leem o pedido com a flag ligada, e o passo grava
 * "não pedir" sempre que a opção não é oferecida ou o membro a desliga.
 */
export const reservedScheduleLinkedScheduleFormAtom =
  atomWithReset<ReservedScheduleLinkedSchedule>({
    openLinkedSchedule: true,
    appointmentTypes: [],
  });

/**
 * Índices de histórico (`__TSR_index`) das telas para as quais a confirmação volta: a listagem e os
 * horários. Cada tela grava o próprio ao montar (`useRecordWizardStop`); a confirmação salta com
 * `go(destino − atual)` (`useReturnToWizardStop`). Não é rascunho, mas é estado do mesmo fluxo e
 * morre com ele: um índice de uma passada anterior apontaria para uma entrada que pode não ser mais
 * a listagem.
 */
export const reservedScheduleWizardHistoryAtom = atomWithReset<
  Partial<Record<"list" | "slots", number>>
>({});

/**
 * Limpa o rascunho inteiro do fluxo de criação de Período Garantido — os cinco atoms são globais e
 * só fazem sentido juntos, então esquecer um deixa duração, unidade, horário, pedido de agenda ou
 * índice de histórico obsoletos vivos para reaparecerem na tentativa seguinte.
 *
 * Chame ao **entrar** no fluxo (hoje, na listagem de PGs, a única porta de entrada) e ao **sair**
 * por sucesso. Não chame em `create/`: `reserved-schedule-slots` linka de volta para lá no meio do
 * caminho, e resetar ali apagaria o rascunho em andamento do membro.
 */
export const useResetReservedScheduleDraft = (): (() => void) => {
  const resetForm = useResetAtom(reservedScheduleFormAtom);
  const resetSlot = useResetAtom(reservedScheduleSlotFormAtom);
  const resetStartingDay = useResetAtom(reservedScheduleStartingDayFormAtom);
  const resetLinkedSchedule = useResetAtom(reservedScheduleLinkedScheduleFormAtom);
  const resetWizardHistory = useResetAtom(reservedScheduleWizardHistoryAtom);

  return useCallback(() => {
    resetForm();
    resetSlot();
    resetStartingDay();
    resetLinkedSchedule();
    resetWizardHistory();
  }, [resetForm, resetSlot, resetStartingDay, resetLinkedSchedule, resetWizardHistory]);
};
